"use client";
import { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, Easing } from "motion/react";
import {
  MapPin,
  Star,
  Clock,
  ChevronRight,
  Heart,
  Flame,
  Coffee,
  Pizza,
  Utensils,
  Search,
  X,
  Filter,
  SlidersHorizontal,
  ArrowLeft,
} from "lucide-react";
import CustomerDashboardLayout from "./Layout/DashboardLayout";
import { useRestaurantStore } from "@/stores/useRestaurantStore";

const THEME = {
  primary: "#2F4858",
  primaryLight: "#3A5A6E",
  primaryDark: "#243845",
  secondary: "#FF8C42",
  secondaryLight: "#FFA26B",
  secondaryDark: "#E67C3A",
  accent: "#FFE156",
  accentLight: "#FFF1A8",
  accentDark: "#EBCD4D",
  success: "#4CAF50",
  successLight: "#81C784",
  error: "#F44336",
  errorLight: "#FF7961",
  info: "#2196F3",
  infoLight: "#64B5F6",
  lightBg: "#F8F9FC",
  darkText: "#1E1E1E",
  mutedText: "#7B7B7B",
  cardBg: "#FFFFFF",
  gradientPrimary: "linear-gradient(135deg, #2F4858, #3A5A6E)",
  gradientSecondary: "linear-gradient(135deg, #FF8C42, #FFA26B)",
  gradientSuccess: "linear-gradient(135deg, #4CAF50, #81C784)",
  modalBg: "rgba(0,0,0,0.5)",
  neutral100: "#F9FAFB",
  neutral200: "#F1F2F4",
};

// Food-related decorative elements for background
const DECORATIVE_ELEMENTS = [
  {
    icon: Coffee,
    position: "top-5 left-3 -rotate-15",
    size: 100,
    opacity: 0.04,
  },
  {
    icon: Pizza,
    position: "top-40 right-4 rotate-20",
    size: 120,
    opacity: 0.04,
  },
  {
    icon: Utensils,
    position: "bottom-10 left-5 rotate-10",
    size: 110,
    opacity: 0.04,
  },
];

// Categories for filtering
const CATEGORIES = [
  "All",
  "American",
  "Italian",
  "Mexican",
  "Chinese",
  "Japanese",
  "Indian",
  "Vegetarian",
  "Fast Food",
];

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: index * 0.05 },
  }),
};

// Price range descriptions for better accessibility
const PRICE_RANGE_DESCRIPTIONS = {
  low: "Budget-friendly",
  medium: "Moderately priced",
  high: "Premium dining",
  veryHigh: "Luxury experience",
};

// Custom debounce hook implementation
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Format price range with accessibility features
const PriceIndicator = ({
  priceRange,
  className = "",
}: {
  priceRange: string;
  className?: string;
}) => {
  const getPriceInfo = (range: string) => {
    switch (range) {
      case "low":
        return {
          level: 1,
          description: PRICE_RANGE_DESCRIPTIONS.low,
        };
      case "medium":
        return {
          level: 2,
          description: PRICE_RANGE_DESCRIPTIONS.medium,
        };
      case "high":
        return {
          level: 3,
          description: PRICE_RANGE_DESCRIPTIONS.high,
        };
      case "veryHigh":
        return {
          level: 4,
          description: PRICE_RANGE_DESCRIPTIONS.veryHigh,
        };
      default:
        return {
          level: 1,
          description: PRICE_RANGE_DESCRIPTIONS.low,
        };
    }
  };

  const priceInfo = getPriceInfo(priceRange);

  return (
    <span
      className={`inline-flex items-center ${className}`}
      aria-label={`Price level: ${priceInfo.description}`}
      title={priceInfo.description}
    >
      <span className="sr-only">{priceInfo.description}</span>
    </span>
  );
};

// Fixed easing types for motion transitions
const EASING_FUNCTIONS = {
  easeOut: [0, 0, 0.58, 1] as Easing,
  easeInOut: [0.42, 0, 0.58, 1] as Easing,
};

// Hover animation properties with valid easing
const subtleHoverProps = {
  y: -5,
  transition: { duration: 0.2, ease: EASING_FUNCTIONS.easeOut },
};

// Restaurant Card Component
const RestaurantCard = ({
  restaurant,
  isFavorite,
  onToggleFavorite,
  index,
  layout = "grid",
}: {
  restaurant: any;
  isFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent, id: string) => void;
  index: number;
  layout?: "grid" | "list";
}) => {
  const isGrid = layout === "grid";

  return (
    <Link
      href={`/dashboards/customer/restaurants/${restaurant.id}`}
      className="group"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{
          opacity: 1,
          scale: 1,
          transition: { delay: index * 0.1 },
        }}
        whileHover={isGrid ? subtleHoverProps : undefined}
        className={`rounded-xl overflow-hidden shadow-md border border-gray-100 transition-all duration-300 group-hover:shadow-lg ${
          isGrid ? "h-full" : "flex"
        }`}
        style={{ backgroundColor: THEME.cardBg }}
        role="article"
        aria-label={`Restaurant: ${restaurant.name}, Rating: ${
          restaurant.averageRating
        } out of 5, Price: ${
          PRICE_RANGE_DESCRIPTIONS[
            restaurant.priceRange as keyof typeof PRICE_RANGE_DESCRIPTIONS
          ] || PRICE_RANGE_DESCRIPTIONS.low
        }`}
      >
        <div
          className={`relative overflow-hidden ${
            isGrid ? "h-36" : "w-1/3 min-h-[120px]"
          }`}
        >
          <Image
            src={`${restaurant.images.cover}`}
            alt={`${restaurant.name} - ${
              restaurant.cuisineTypes?.join(", ") || "Restaurant"
            }`}
            fill
            sizes={
              isGrid
                ? "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                : "33vw"
            }
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            priority={index < 2}
          />
          <div
            className={`absolute inset-0 ${
              isGrid
                ? "bg-gradient-to-t from-black/30"
                : "bg-gradient-to-r from-black/10"
            } to-transparent`}
          />
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={(e) => onToggleFavorite(e, restaurant.id)}
            className="absolute top-2 right-2 bg-white/90 border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer shadow-md z-10"
            aria-label={
              isFavorite
                ? `Remove ${restaurant.name} from favorites`
                : `Add ${restaurant.name} to favorites`
            }
          >
            <Heart
              size={14}
              className={
                isFavorite ? "text-red-500 fill-red-500" : "text-gray-500"
              }
            />
          </motion.button>
          {!isGrid && restaurant.status && (
            <div
              className={`absolute top-2 left-2 text-white px-2 py-1 rounded-full text-xs font-medium ${
                restaurant.status === "active" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {restaurant.status === "active" ? "Open" : "Closed"}
            </div>
          )}
          {isGrid && (
            <div className="absolute bottom-2 left-2 bg-white/90 rounded-md px-2 py-1 flex items-center">
              <Star
                size={12}
                className="text-yellow-500 fill-yellow-500 mr-1"
                aria-hidden="true"
              />
              <span className="text-sm font-semibold">
                {(restaurant.averageRating ?? 0).toFixed(1)}
              </span>
            </div>
          )}
        </div>
        <div className={isGrid ? "p-4" : "w-2/3 p-4"}>
          <div className="flex justify-between items-start mb-2">
            <h3
              className={`font-bold text-gray-900 ${
                isGrid ? "text-base" : "text-lg"
              }`}
            >
              {restaurant.name}
            </h3>
            {isGrid && (
              <PriceIndicator
                priceRange={restaurant.priceRange || "low"}
                className="font-medium"
              />
            )}
          </div>
          <div className="flex items-center gap-1 mb-2">
            <Star
              size={12}
              className="text-yellow-400 fill-yellow-400"
              aria-hidden="true"
            />
            <span className="font-semibold text-gray-900 text-xs">
              {(restaurant.averageRating ?? 0).toFixed(1)}
            </span>
            <span className="text-gray-500 text-xs">
              ({restaurant.totalReviews} reviews)
            </span>
          </div>
          {!isGrid && (
            <PriceIndicator
              priceRange={restaurant.priceRange || "low"}
              className="font-medium mb-2"
            />
          )}
          {restaurant.cuisineTypes && (
            <div className="flex flex-wrap gap-1 mb-2">
              {restaurant.cuisineTypes
                .slice(0, 2)
                .map((cuisine: string, idx: number) => (
                  <span
                    key={idx}
                    className="py-1 px-2 rounded-md text-xs font-medium"
                    style={{
                      backgroundColor: `${THEME.secondaryLight}15`,
                      color: THEME.secondaryDark,
                    }}
                  >
                    {cuisine}
                  </span>
                ))}
            </div>
          )}
          <div className="flex justify-between items-center text-gray-500 text-xs">
            <span className="flex items-center gap-1">
              <MapPin size={12} aria-hidden="true" />
              <span>2.4 km</span>
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} aria-hidden="true" />
              <span>{restaurant.averageVisitDuration || 45} min</span>
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

// All Restaurants View Component
const AllRestaurantsView = ({
  restaurants,
  favorites,
  toggleFavorite,
  onCollapse,
  currentDate,
}: {
  restaurants: any[];
  favorites: string[];
  toggleFavorite: (e: React.MouseEvent, id: string) => void;
  onCollapse: () => void;
  currentDate: string;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortOption, setSortOption] = useState("rating");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Filter restaurants based on search and category
  const filteredRestaurants = useMemo(() => {
    let filtered = restaurants;

    // Filter by search term
    if (debouncedSearchTerm) {
      filtered = filtered.filter(
        (restaurant) =>
          restaurant.name
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()) ||
          restaurant.tags?.some((tag: string) =>
            tag.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
          )
      );
    }

    // Filter by category
    if (activeCategory !== "All") {
      filtered = filtered.filter((restaurant) =>
        restaurant.tags?.includes(activeCategory.toLowerCase())
      );
    }

    // Sort results
    if (sortOption === "rating") {
      filtered = [...filtered].sort(
        (a, b) => (b.averageRating ?? 0) - (a.averageRating ?? 0)
      );
    } else if (sortOption === "name") {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }

    return filtered;
  }, [restaurants, debouncedSearchTerm, activeCategory, sortOption]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mb-10"
      role="region"
      aria-label="All restaurants view"
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            All Restaurants
          </h1>
          <p className="text-sm text-gray-500 mt-1">{currentDate}</p>
        </div>
        <motion.button
          whileHover={{ backgroundColor: THEME.neutral200 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCollapse}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Collapse all restaurants view"
        >
          <ArrowLeft size={20} className="text-gray-700" aria-hidden="true" />
        </motion.button>
      </div>

      {/* Search & Filters */}
      <div
        className="p-4 bg-gray-50 rounded-xl mb-6"
        style={{ backgroundColor: THEME.neutral100 }}
      >
        <div className="relative mb-4">
          <label htmlFor="all-restaurants-search" className="sr-only">
            Search all restaurants
          </label>
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
            aria-hidden="true"
          />
          <input
            id="all-restaurants-search"
            type="text"
            placeholder="Search restaurants, cuisines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-3 border text-gray-600 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
          />
          {searchTerm && (
            <motion.button
              whileHover={{ backgroundColor: THEME.neutral200 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full"
              aria-label="Clear search"
            >
              <X size={16} className="text-gray-500" aria-hidden="true" />
            </motion.button>
          )}
        </div>

        {/* Categories Scrollable */}
        <div
          className="flex overflow-x-auto pb-2 gap-2 hide-scrollbar"
          role="group"
          aria-label="Restaurant categories"
        >
          {CATEGORIES.map((category) => (
            <motion.button
              key={category}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(category)}
              className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === category
                  ? "bg-orange-500 text-white"
                  : "bg-white text-gray-700 border border-gray-200"
              }`}
              aria-pressed={activeCategory === category}
            >
              {category}
            </motion.button>
          ))}
        </div>

        {/* Sort Controls */}
        <div className="flex justify-between items-center mt-4 flex-wrap gap-2">
          <p className="text-sm text-gray-500 flex items-center">
            <Filter size={16} className="mr-1" aria-hidden="true" />
            {filteredRestaurants.length} results found
          </p>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600" id="sort-label">
              Sort by:
            </span>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="px-3 py-2 border text-gray-600 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              aria-labelledby="sort-label"
            >
              <option value="rating">Top Rated</option>
              <option value="name">Name (A-Z)</option>
              <option value="distance">Distance</option>
              <option value="deliveryTime">Delivery Time</option>
            </select>
          </div>
        </div>
      </div>

      {/* Restaurants Grid */}
      <div className="overflow-y-auto">
        {filteredRestaurants.length === 0 ? (
          <div className="text-center py-12">
            <SlidersHorizontal
              size={40}
              className="mx-auto text-gray-300 mb-4"
              aria-hidden="true"
            />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No restaurants found
            </h3>
            <p className="text-gray-500 mb-6">
              Try adjusting your search or filters to find what you're looking
              for.
            </p>
            <motion.button
              whileHover={{ backgroundColor: THEME.primaryDark }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setSearchTerm("");
                setActiveCategory("All");
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium"
            >
              Clear Filters
            </motion.button>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            role="list"
            aria-label="Restaurant listings"
          >
            {filteredRestaurants.map((restaurant, index) => (
              <motion.div
                key={restaurant.id}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={itemVariants}
                role="listitem"
                className="group"
              >
                <RestaurantCard
                  restaurant={restaurant}
                  isFavorite={favorites.includes(restaurant.id)}
                  onToggleFavorite={toggleFavorite}
                  index={index}
                  layout="grid"
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default function CustomerDashboardPage() {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [animateCards, setAnimateCards] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAllRestaurants, setShowAllRestaurants] = useState(false);
  const [currentDate] = useState(
    new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  );

  const { restaurants, isLoading, fetchRestaurants } = useRestaurantStore();
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Handle scroll for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem("restaurantFavorites");
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage when they change
  useEffect(() => {
    localStorage.setItem("restaurantFavorites", JSON.stringify(favorites));
  }, [favorites]);

  // Trigger staggered animation after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const toggleFavorite = useCallback(
    (event: React.MouseEvent, restaurantId: string) => {
      event.preventDefault();
      event.stopPropagation();

      // Add subtle haptic feedback if available (for mobile)
      if (navigator.vibrate) {
        navigator.vibrate(40);
      }

      setFavorites((prev) =>
        prev.includes(restaurantId)
          ? prev.filter((id) => id !== restaurantId)
          : [...prev, restaurantId]
      );
    },
    []
  );

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Filter and sort restaurants based on search and category
  const filteredRestaurants = useMemo(() => {
    let filtered = restaurants;

    if (debouncedSearchQuery) {
      filtered = filtered.filter(
        (restaurant) =>
          restaurant.name
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          restaurant.tags?.some((tag: string) =>
            tag.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
          )
      );
    }

    if (selectedCategory !== "All") {
      filtered = filtered.filter((restaurant) =>
        restaurant.tags?.includes(selectedCategory.toLowerCase())
      );
    }

    return filtered;
  }, [restaurants, debouncedSearchQuery, selectedCategory]);

  const popularRestaurants = useMemo(
    () =>
      [...filteredRestaurants]
        .sort((a, b) => (b.averageRating ?? 0) - (a.averageRating ?? 0))
        .slice(0, 4),
    [filteredRestaurants]
  );

  const favoriteRestaurants = useMemo(
    () => filteredRestaurants.filter((r) => favorites.includes(r.id)),
    [filteredRestaurants, favorites]
  );

  const categories = useMemo(() => {
    const allCategories = new Set(restaurants.flatMap((r) => r.tags || []));
    return ["All", ...Array.from(allCategories)].slice(0, 9);
  }, [restaurants]);

  const handleViewAllRestaurants = () => {
    setShowAllRestaurants(!showAllRestaurants);
  };

  if (isLoading) {
    return (
      <CustomerDashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2"
            style={{ borderColor: THEME.primary }}
            aria-label="Loading restaurants"
          ></div>
        </div>
      </CustomerDashboardLayout>
    );
  }

  return (
    <CustomerDashboardLayout>
      <div
        className="min-h-screen bg-gray-50 pb-16 md:pb-8 relative overflow-hidden"
        style={{ backgroundColor: THEME.lightBg }}
      >
        {/* Decorative background elements */}
        <div className="hidden lg:block">
          {DECORATIVE_ELEMENTS.map((elem: any, index: number) => {
            const Icon = elem.icon;
            return (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  rotate: parseInt(elem.position.split(" ")[2] || "0"),
                }}
                animate={{
                  opacity: elem.opacity,
                  rotate: parseInt(elem.position.split(" ")[2] || "0"),
                }}
                transition={{ delay: index * 0.3 + 0.5 }}
                className={`absolute ${elem.position} z-0`}
                style={{ color: THEME.primaryDark }}
                aria-hidden="true"
              >
                <Icon size={elem.size} />
              </motion.div>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className=" overflow-x-auto hide-scrollbar"
            role="region"
            aria-label="Restaurant categories"
          >
            <div className="flex space-x-3 pb-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={{
                    backgroundColor:
                      selectedCategory === category
                        ? THEME.secondary
                        : THEME.neutral100,
                  }}
                  aria-pressed={selectedCategory === category}
                >
                  {category === "All" ? "All" : category}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Conditional rendering based on showAllRestaurants state */}
          {showAllRestaurants ? (
            <AllRestaurantsView
              restaurants={filteredRestaurants}
              favorites={favorites}
              toggleFavorite={toggleFavorite}
              onCollapse={() => setShowAllRestaurants(false)}
              currentDate={currentDate}
            />
          ) : (
            <>
              {/* Featured Carousel */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-10"
              >
                <div className="flex justify-between items-center mb-5">
                  <h2
                    className="text-xl md:text-2xl font-bold flex items-center gap-2"
                    style={{ color: THEME.darkText }}
                  >
                    <Flame
                      size={22}
                      className="text-orange-500"
                      aria-hidden="true"
                    />
                    Featured Restaurants
                  </h2>
                  <motion.button
                    whileHover={{ backgroundColor: `${THEME.secondary}15` }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleViewAllRestaurants}
                    className="bg-transparent border-none flex items-center gap-1 font-semibold py-2 px-3 rounded-lg transition-colors"
                    style={{ color: THEME.secondary }}
                    aria-label="View all featured restaurants"
                  >
                    View All <ChevronRight size={18} aria-hidden="true" />
                  </motion.button>
                </div>
              </motion.div>

              {/* Popular Restaurants */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-10"
                role="region"
                aria-label="Popular restaurants near you"
              >
                <h2
                  className="text-xl md:text-2xl font-bold mb-5 flex items-center gap-2"
                  style={{ color: THEME.darkText }}
                >
                  <Star
                    size={22}
                    className="text-yellow-500 fill-yellow-500"
                    aria-hidden="true"
                  />
                  Popular Near You
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {popularRestaurants.map((restaurant, index) => (
                    <motion.div
                      key={restaurant.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={
                        animateCards
                          ? {
                              opacity: 1,
                              scale: 1,
                              transition: { delay: index * 0.1 },
                            }
                          : {}
                      }
                      className="group"
                    >
                      <RestaurantCard
                        restaurant={restaurant}
                        isFavorite={favorites.includes(restaurant.id)}
                        onToggleFavorite={toggleFavorite}
                        index={index}
                        layout="grid"
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Favorites Section */}
              {favoriteRestaurants.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mb-10"
                  role="region"
                  aria-label="Your favorite restaurants"
                >
                  <h2
                    className="text-xl md:text-2xl font-bold mb-5 flex items-center gap-2"
                    style={{ color: THEME.darkText }}
                  >
                    <Heart
                      size={22}
                      className="text-red-500 fill-red-500"
                      aria-hidden="true"
                    />
                    Your Favorites
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {favoriteRestaurants.map((restaurant, index) => (
                      <motion.div
                        key={restaurant.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={
                          animateCards
                            ? {
                                opacity: 1,
                                x: 0,
                                transition: { delay: index * 0.1 },
                              }
                            : {}
                        }
                        className="group"
                      >
                        <RestaurantCard
                          restaurant={restaurant}
                          isFavorite={favorites.includes(restaurant.id)}
                          onToggleFavorite={toggleFavorite}
                          index={index}
                          layout="list"
                        />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>

        <style jsx>{`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }

          /* High contrast mode styles */
          .high-contrast {
            --tw-bg-opacity: 1;
            background-color: rgb(0 0 0 / var(--tw-bg-opacity)) !important;
            color: white !important;
          }

          .high-contrast .text-gray-900 {
            color: white !important;
          }

          .high-contrast .text-gray-500 {
            color: #ccc !important;
          }

          .high-contrast .bg-white {
            background-color: black !important;
            color: white !important;
            border-color: white !important;
          }

          .high-contrast .shadow-md {
            box-shadow: 0 4px 6px -1px white, 0 2px 4px -2px white !important;
          }
        `}</style>
      </div>
    </CustomerDashboardLayout>
  );
}
